import React, { useRef, useState, useEffect } from "react";
import { Input, Icon } from "semantic-ui-react";

type Props = {
  value?: string;
  onChange: (v: string) => void;
  disabled?: boolean;
};

const TimeInput: React.FC<Props> = ({ value = "__:__", onChange, disabled }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [digits, setDigits] = useState<string[]>(["_", "_", "_", "_"]);

  useEffect(() => {
    if (!value) return;
    const [hh = "", mm = ""] = value.split(":");
    setDigits([hh[0] || "_", hh[1] || "_", mm[0] || "_", mm[1] || "_"]);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const el = e.target;
    let pos = el.selectionStart || 0;
    let char = (e.nativeEvent as InputEvent).data;

    if (char !== null && !/\d/.test(char)) return;

    let index = pos <= 3 ? pos - 1 : pos - 2;

    if (!char) return;
    if (index < 0 || index > 3) return;

    const newDigits = [...digits];
    if (index === 1 && newDigits[0] === "_") {
      newDigits[0] = "0";
    }

    if (index === 3 && newDigits[2] === "_") {
      newDigits[2] = "0";
    }
    newDigits[index] = char;
    const [h1, h2, m1, m2] = newDigits;

    const isValidH1 = ["0", "1", "2"].includes(h1);
    const isValidH2 = !(h1 === "2" && parseInt(h2) > 3);
    const isValidM1 = parseInt(m1) <= 5 || m1 === "_";

    if (!isValidH1 || !isValidH2 || !isValidM1) {
      requestAnimationFrame(() => {
        if (!inputRef.current) return;
        inputRef.current.setSelectionRange(pos, pos - 1);
      });
      return;
    }
    setDigits(newDigits);
    const hh = `${newDigits[0]}${newDigits[1]}`;
    const mm = `${newDigits[2]}${newDigits[3]}`;
    onChange(`${hh}:${mm}`);
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      let nextPos = pos;
      if (nextPos === 3) nextPos++;
      inputRef.current.setSelectionRange(nextPos, nextPos);
    });
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const el = inputRef.current;
    if (!el) return;

    const pos = el.selectionStart || 0;

    if (e.key === "Backspace") {
      e.preventDefault();
      let index = pos <= 2 ? pos - 1 : pos - 2;

      if (index < 0) return;

      const newDigits = [...digits];
      newDigits[index] = "_";
      setDigits(newDigits);
      const hh = `${newDigits[0]}${newDigits[1]}`;
      const mm = `${newDigits[2]}${newDigits[3]}`;
      onChange(`${hh}:${mm}`);
      requestAnimationFrame(() => {
        if (!inputRef.current) return;

        let newPos = pos - 1;

        if (newPos === 2) newPos--;
        inputRef.current.setSelectionRange(newPos, newPos);
      });
    }
  };
  const setNow = () => {
    if (disabled) return;

    const now = new Date();
    const hh = now.getHours().toString().padStart(2, "0");
    const mm = now.getMinutes().toString().padStart(2, "0");
    const newDigits = [hh[0], hh[1], mm[0], mm[1]];
    setDigits(newDigits);
    onChange(`${hh}:${mm}`);
    requestAnimationFrame(() => {
      if (!inputRef.current) return;

      inputRef.current.setSelectionRange(5, 5);
      inputRef.current.focus();
    });
  };
  return (
    <Input
      input={{
        ref: inputRef,
        value: `${digits[0]}${digits[1]}:${digits[2]}${digits[3]}`,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
      }}
      placeholder="HH:mm"
      disabled={disabled}
      icon={<Icon name="clock" link={!disabled} onClick={setNow} />}
      style={{ width: "120px" }}
    />
  );
};

export default TimeInput;
